import NavigationItemInterface from "@app/Interface/NavigationItemInterface";
import NavigationItemModel from "@app/Model/NavigationIteModel";
import axios from "@app/Security/AxiosAuthentication";
import { AppRouteConfig, IAppRoute } from "@app/routes";
import ApiEndpoints from "@app/utils/ApiEndpoints";

// react components
import { PageTemplate } from '@app/Page/PageTemplate';

import htmlParser from "@app/utils/htmlParser";
import { PermissionDenied } from "@app/PermissionDenied/PermissionDenied";
import { NotFound } from "@app/NotFound/NotFound";
import RollbarHelper from "@app/utils/RollbarHelper";
import ApiCache from "./ApiCache";
import AppSettings from "@app/utils/AppSettings";

// TODO: Look into changing title based on member or non-member signin
const websiteTitleText = "Northwest Dairy Association";

export default class NavigationHelper {

    /**
     * Sends request to api to get a navigation for the users, 
     * with optional currently selected fpuId
     * 
     * @param controller AbortControllerfpuId - handles if component unmounts or if cancel button is added
     * @param fpuId currently selected FPU id (producer id)
     * @returns Array<NavigationItemInterface>
     */
    static getNavigation = async (controller: AbortController, fpuId: number, path: string): Promise<Array<NavigationItemInterface>> => {

        const baseUrl: string = process.env.API_BASEURL ?? "";

        const endPoint: string = ApiEndpoints.NAVIGATION_ITEMS + "?page=1";

        let url = "";

        if (fpuId > 0) {

            url = `${baseUrl}${endPoint}&fpu_id=${fpuId}`;

        } else {

            url = `${baseUrl}${endPoint}`;

        }

        if (path) {
            // TODO: Remove ?cat=DDD if it exists. 
            url += `&path=${path}`;
        }

        const cacheData = ApiCache.get(url);

        if (cacheData) {
            const cachedNavigationData: NavigationItemInterface[] = JSON.parse(cacheData.content);

            return cachedNavigationData;
        }

        return await axios.get(url, {
            withCredentials: true,
            signal: controller.signal
        })
            .then(response => {

                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const dataItems: any = response.data["hydra:member"];

                const navItems = NavigationHelper.getNavigationItemsFromData(dataItems);

                // save to cache
                ApiCache.set(url, navItems, AppSettings.getPageCacheStaleMinutes())

                return navItems;

            })
            .catch(error => {

                RollbarHelper.logRequestError(error, "getNavigation()");

                // return an empty set of navItems
                const emptyItems: Array<NavigationItemInterface> = [];

                return emptyItems;

            });

    }

    /**
     * Sends request to api to get a navigation for the users, 
     * with optional currently selected fpuId
     * 
     * @param fpuId currently selected FPU id (producer id)
     * @returns Array<NavigationItemInterface>
     */
     static getNavigationNonAsync = (fpuId: number, path: string): void => {

        const baseUrl: string = process.env.API_BASEURL ?? "";

        const endPoint: string = ApiEndpoints.NAVIGATION_ITEMS + "?page=1";

        let url = "";

        if (fpuId > 0) {

            url = `${baseUrl}${endPoint}&fpu_id=${fpuId}`;

        } else {

            url = `${baseUrl}${endPoint}`;

        }

        if (path) {
            // TODO: Remove ?cat=DDD if it exists. 
            url += `&path=${path}`;
        }


        axios.get(url, {
            withCredentials: true
        })
            .then(response => {

                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const dataItems: any = response.data["hydra:member"];

                const navItems = NavigationHelper.getNavigationItemsFromData(dataItems);

                // save to cache
                ApiCache.set(url, navItems, AppSettings.getPageCacheStaleMinutes())

            })
            .catch(error => {

                RollbarHelper.logRequestError(error, "getNavigationNonAsync()");

            });

    }

    /**
     * Takes array of dataItems in the JSON response.
     * The JSON response needs to match the symfony middleware endpoint response 
     * for /api/navigation
     * 
     * @param members 
     * @returns 
     */
    static getNavigationItemsFromData(members: Array<NavigationItemInterface>): Array<NavigationItemInterface> {

        const navItems: Array<NavigationItemInterface> = [];

        members.forEach((member) => {

            const navItem = new NavigationItemModel();
            navItem.id = member.id;
            navItem.menu_item_id = member.menu_item_id;
            navItem.menu_order = member.menu_order;
            navItem.menu_item_parent = member.menu_item_parent;
            navItem.object_id = member.object_id;
            navItem.object = member.object;
            navItem.type = member.type;
            navItem.type_label = member.type_label;
            navItem.url = member.url;
            navItem.path = member.path;
            navItem.title = htmlParser.htmlDecode(member.title);
            navItem.target = member.target;
            navItem.description = member.description;
            navItem.classes = member.classes;

            if (Array.isArray(member.children) && member.children.length > 0) {

                navItem.children = this.getNavigationItemsFromData(member.children);

            } else {

                navItem.children = [];
            }

            navItems.push(navItem);

        });

        return navItems;
    }

    /**
     * Converts navigation items from middleware endpoing
     * into an array of AppRouteConfig, which has two types: IAppRoute | IAppRouteGroup
     * 
     * @param items 
     * @returns AppRouteConfig[]
     */
    private static convertNavItemsToRoutes(items: NavigationItemInterface[]): AppRouteConfig[] {

        const navRoutes: AppRouteConfig[] = [];

        for (const item of items) {

            if (item.children.length > 0) {

                // collapsable section
                const childRoutes: AppRouteConfig[] = this.getChildrenAppRoutes(item.children);

                navRoutes.push({
                    label: item.title,
                    groupPath: item.path,
                    routes: childRoutes
                });


            } else {

                navRoutes.push({
                    component: PageTemplate,
                    exact: true,
                    label: item.title,
                    path: item.path,
                    pageId: item.object_id,
                    title: websiteTitleText + " | " + item.title,
                });

            }

        }

        return navRoutes;

    }

    /**
     * Gets a list of children app routes for the IAppGroup type 
     * NOTE: Due to type and interface definitions, we're unable to check 
     * for Children being a group with child routes. 
     * see: https://learnwithparam.com/blog/dynamic-pages-in-react-router/
     * 
     * @param items 
     * @returns IAppRoute[]
     */
    private static getChildrenAppRoutes(items: NavigationItemInterface[]): AppRouteConfig[] {

        const navRoutes: AppRouteConfig[] = [];

        // TODO: Look into changing title based on member or non-member signin
        const websiteTitleText = process.env.NDA_TITLE ?? "Northwest Dairy Association";

        for (const item of items) {

            if (item.children.length) {

                // collapsable section
                const childRoutes: AppRouteConfig[] = this.getChildrenAppRoutes(item.children);

                navRoutes.push({
                    label: item.title,
                    groupPath: item.path,
                    routes: childRoutes
                });

            } else {

                navRoutes.push({
                    component: PageTemplate,
                    exact: true,
                    label: item.title,
                    path: item.path,
                    pageId: item.object_id,
                    title: websiteTitleText + " | " + item.title,
                });

            }

        }

        return navRoutes;

    }

    static getAppRoutesForUser = async (controller: AbortController, fpuId: number): Promise<AppRouteConfig[]> => {

        const items = await NavigationHelper.getNavigation(controller, fpuId, "");

        const navRoutes: AppRouteConfig[] = NavigationHelper.convertNavItemsToRoutes(items);

        return navRoutes;

    }

    /**
     * Sets up handle for dynamic routing
     * The PageTemplate will determine what to do based on handle value
     * 
     * @returns AppRouteConfig[]
     */
    static getDynamicAppRoutes = (): AppRouteConfig[] => {

        const navRoutes: AppRouteConfig[] = [
            {
                label: "Dashboard",
                component: PageTemplate,
                exact: true,
                path: "/",
                pageId: 0,
                title: "Dashboard | " + websiteTitleText,
                isAsync: false,
                routes: undefined
            },
            {
                label: "Permission Denied",
                component: PermissionDenied,
                exact: true,
                path: "/permission-denied",
                pageId: 0,
                title: "Permission Denied | " + websiteTitleText,
                isAsync: false,
                routes: undefined
            },
            {
                label: "Page Not Found",
                component: NotFound,
                exact: true,
                path: "/page-not-found",
                pageId: 0,
                title: "Page Not Found | " + websiteTitleText,
                isAsync: false,
                routes: undefined
            },
           {
                label: "Dyname Route",
                component: PageTemplate,
                exact: false,
                path: "/:handle",
                pageId: 0,
                title: websiteTitleText,
                isAsync: false,
                routes: undefined
            }
        ];

        return navRoutes;

    }

    static getAppRoutesFromRouteConfig = (routes: AppRouteConfig[]): IAppRoute[] => {

        let flattenedRoutes: IAppRoute[] = [];

        routes.forEach((route) => {

            if ("path" in route) {

                const flatRoute: IAppRoute = {
                    label: route.label,
                    component: route.component,
                    exact: route.exact,
                    path: route.path,
                    pageId: route.pageId,
                    title: route.title,
                    isAsync: route.isAsync,
                    routes: undefined
                };

                flattenedRoutes.push(flatRoute);

            } else if (Array.isArray(route.routes)) {

                const childRoutes = NavigationHelper.getAppRoutesFromRouteConfig(route.routes);

                // copy the childroutes into flattened routes
                flattenedRoutes = [...flattenedRoutes, ...childRoutes];

            }

        });

        return flattenedRoutes;

    }

}